import { memo } from 'react';
import { Search, FilterX, FileCheck, Archive } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { INVESTOR_TYPES, KYC_STATUS_OPTIONS } from '@/types/investor';
import type { InvestorFilters } from '@/types/investor';
import { useFunds } from '@/hooks/react-query/useFunds';

interface InvestorsFiltersProps {
  filters: InvestorFilters;
  searchValue?: string;
  onFilterChange: (
    key: keyof InvestorFilters | 'fund' | 'pending_changes',
    value: string | undefined | boolean
  ) => void;
  onClearFilters: () => void;
  showPendingChangesOnly?: boolean;
}

export const InvestorsFilters = memo(function InvestorsFilters({
  filters,
  searchValue,
  onFilterChange,
  onClearFilters,
  showPendingChangesOnly = false,
}: InvestorsFiltersProps) {
  const currentSearchValue = searchValue ?? filters.search ?? '';
  const { data: funds = [] } = useFunds();

  const hasActiveFilters =
    filters.investor_type ||
    filters.kyc_status ||
    currentSearchValue ||
    filters.fund ||
    showPendingChangesOnly ||
    filters.include_archived;

  return (
    <div className="flex flex-wrap items-center gap-3 lg:flex-nowrap">
      <div className="relative min-w-48 flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          id="investor-search"
          placeholder="Search investors or contacts... (Press / to focus, ↑↓ to navigate)"
          value={currentSearchValue}
          onChange={(e) => onFilterChange('search', e.target.value)}
          onKeyDown={(e) => {
            if (!['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(e.key)) {
              e.nativeEvent.stopImmediatePropagation();
            }
          }}
          className="pl-9"
        />
      </div>

      <Select
        value={filters.investor_type || 'all'}
        onValueChange={(value) =>
          onFilterChange('investor_type', value === 'all' ? undefined : value)
        }
      >
        <SelectTrigger className="w-36 shrink-0">
          <SelectValue placeholder="Type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          {Object.entries(INVESTOR_TYPES).map(([key, label]) => (
            <SelectItem key={key} value={key}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.kyc_status || 'all'}
        onValueChange={(value) =>
          onFilterChange('kyc_status', value === 'all' ? undefined : value)
        }
      >
        <SelectTrigger className="w-36 shrink-0">
          <SelectValue placeholder="KYC Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Status</SelectItem>
          {Object.entries(KYC_STATUS_OPTIONS).map(([key, label]) => (
            <SelectItem key={key} value={key}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.fund || 'all'}
        onValueChange={(value) =>
          onFilterChange('fund', value === 'all' ? undefined : value)
        }
      >
        <SelectTrigger className="w-56 shrink-0">
          <SelectValue placeholder="Fund" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Funds</SelectItem>
          {funds.map((fund) => (
            <SelectItem key={fund.id} value={fund.id}>
              {fund.display_name || fund.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        variant={showPendingChangesOnly ? 'default' : 'outline'}
        onClick={() =>
          onFilterChange('pending_changes', !showPendingChangesOnly)
        }
        className={cn(
          'shrink-0 gap-2',
          showPendingChangesOnly && 'bg-orange-600 hover:bg-orange-700'
        )}
      >
        <FileCheck className="h-4 w-4" />
        Pending Changes
      </Button>

      <Button
        variant={filters.include_archived ? 'default' : 'outline'}
        onClick={() =>
          onFilterChange('include_archived', !filters.include_archived)
        }
        className="shrink-0 gap-2"
      >
        <Archive className="h-4 w-4" />
        Show Archived
      </Button>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onClearFilters}
          aria-label="Clear filters"
          title="Clear filters"
          className="shrink-0"
        >
          <FilterX className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
});
